import { ActionIcon, Badge, Button, Card, Grid, Group, Modal, NavLink, ScrollArea, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconFolder, IconFolderOpen, IconPlus, IconTrash, IconWorld } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  LIBRARY_LABELS,
  libraryTree,
  PHARMA_COUNTRY_CATEGORIES,
  PHARMA_COUNTRY_LEAVES,
  pharmaCountryKey,
  resolveLibraryLeaf,
  type AppModule,
  type FileRefDto,
  type LibraryFileDto,
  type LibraryKey,
  type LibraryNode,
  type LibraryTreeDto,
} from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, LoadingState } from '@/components/States';
import { FileRow } from '@/features/files/FileList';
import { FileUploader } from '@/features/files/FileUploader';
import { CountrySelect } from '@/features/requests/components/RequestFields';
import { formatDate } from '@/lib/format';
import { useAction } from '@/lib/useAction';

const MODULE_OF: Record<LibraryKey, AppModule> = { pharmacopoeia: 'Pharmacopoeia', technical_documents: 'TechnicalDocuments' };
const DESCRIPTIONS: Record<LibraryKey, string> = {
  pharmacopoeia: 'Pharmacopoeias, reference books, and each country’s guidelines and checklists.',
  technical_documents: 'QA, QC, FND and Production reference documents: SOPs, specifications, STPs, COAs, BMR/BPR.',
};

interface Selection {
  key: string;
  country: string | null;
}

function countOf(node: LibraryNode, counts: Map<string, number>): number {
  return node.children ? node.children.reduce((sum, c) => sum + countOf(c, counts), 0) : (counts.get(node.key) ?? 0);
}

function TreeNav({ nodes, counts, selected, onSelect }: { nodes: LibraryNode[]; counts: Map<string, number>; selected: string | null; onSelect: (key: string) => void }) {
  return (
    <>
      {nodes.map((n) => {
        const count = countOf(n, counts);
        const within = !!selected && (selected === n.key || selected.startsWith(`${n.key}/`));
        return n.children ? (
          <NavLink
            key={n.key}
            label={n.label}
            leftSection={within ? <IconFolderOpen size={16} /> : <IconFolder size={16} />}
            rightSection={count ? <Badge size="xs" variant="light">{count}</Badge> : null}
            defaultOpened={within}
            childrenOffset={14}
            style={{ borderRadius: 'var(--mantine-radius-md)' }}
          >
            <TreeNav nodes={n.children} counts={counts} selected={selected} onSelect={onSelect} />
          </NavLink>
        ) : (
          <NavLink
            key={n.key}
            label={n.label}
            active={selected === n.key}
            onClick={() => onSelect(n.key)}
            rightSection={count ? <Badge size="xs" variant="light">{count}</Badge> : null}
            style={{ borderRadius: 'var(--mantine-radius-md)' }}
          />
        );
      })}
    </>
  );
}

function CountryPicker({ opened, onClose, onPick }: { opened: boolean; onClose: () => void; onPick: (s: Selection) => void }) {
  const [country, setCountry] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>('herbal');
  const [leaf, setLeaf] = useState<string | null>('guidelines');
  return (
    <Modal opened={opened} onClose={onClose} title="Open a country folder">
      <Stack>
        <CountrySelect value={country} onChange={setCountry} />
        <Select label="Category" data={PHARMA_COUNTRY_CATEGORIES.map((c) => ({ value: c.key, label: c.label }))} value={category} onChange={setCategory} />
        <Select label="Folder" data={PHARMA_COUNTRY_LEAVES.map((c) => ({ value: c.key, label: c.label }))} value={leaf} onChange={setLeaf} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!country || !category || !leaf}
            onClick={() => {
              onPick({ key: pharmaCountryKey(country!, category!, leaf!), country });
              onClose();
            }}
          >
            Open
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** A fixed folder tree of reference files (Pharmacopoeia & Guidelines, Technical Documents). */
export function LibraryPage({ library }: { library: LibraryKey }) {
  const { can } = useAuth();
  const module = MODULE_OF[library];
  const [selected, setSelected] = useState<Selection | null>(null);
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(false);
  const [pending, setPending] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);

  const tree = useQuery({ queryKey: ['library', library, 'tree'], queryFn: () => http.get<LibraryTreeDto>(`/libraries/${library}/tree`) });
  const files = useQuery({
    queryKey: ['library', library, 'files', selected?.key],
    queryFn: () => http.get<{ items: LibraryFileDto[] }>(`/libraries/${library}/files`, { nodeKey: selected!.key }),
    enabled: !!selected,
  });
  const counts = useMemo(() => new Map((tree.data?.counts ?? []).map((c) => [c.key, c.count])), [tree.data]);
  const path = selected ? resolveLibraryLeaf(library, selected.key, selected.country) : null;

  const attach = useAction(
    () => http.post(`/libraries/${library}/files`, { nodeKey: selected!.key, country: selected!.country ?? undefined, fileIds: pending.map((f) => f.id) }),
    {
      success: 'Files added',
      invalidate: [['library', library]],
      onDone: () => {
        setPending([]);
        setAdding(false);
      },
    },
  );
  const remove = useAction((id: string) => http.post(`/libraries/${library}/files/${id}/delete`), { success: 'File removed', invalidate: [['library', library]] });

  return (
    <>
      <PageHeader title={LIBRARY_LABELS[library]} description={DESCRIPTIONS[library]} />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
          <Card p="xs">
            <ScrollArea.Autosize mah={{ base: 320, md: 'calc(100dvh - 220px)' } as unknown as number}>
              {tree.isLoading ? (
                <LoadingState />
              ) : (
                <TreeNav nodes={libraryTree(library)} counts={counts} selected={selected?.key ?? null} onSelect={(key) => setSelected({ key, country: null })} />
              )}
              {library === 'pharmacopoeia' && (
                <NavLink label="Countries" leftSection={<IconWorld size={16} />} defaultOpened childrenOffset={14} style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                  {(tree.data?.countries ?? []).map((c) => (
                    <NavLink key={c.slug} label={c.name} rightSection={<Badge size="xs" variant="light">{c.count}</Badge>} childrenOffset={14} defaultOpened={selected?.country === c.name}>
                      {PHARMA_COUNTRY_CATEGORIES.map((cat) =>
                        PHARMA_COUNTRY_LEAVES.map((leaf) => {
                          const key = pharmaCountryKey(c.name, cat.key, leaf.key);
                          return (
                            <NavLink
                              key={key}
                              label={`${cat.label} · ${leaf.label}`}
                              active={selected?.key === key}
                              rightSection={counts.get(key) ? <Badge size="xs" variant="light">{counts.get(key)}</Badge> : null}
                              onClick={() => setSelected({ key, country: c.name })}
                            />
                          );
                        }),
                      )}
                    </NavLink>
                  ))}
                  <NavLink label="Open another country…" leftSection={<IconPlus size={14} />} onClick={() => setPicking(true)} c="brand" />
                </NavLink>
              )}
            </ScrollArea.Autosize>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
          <Card p={0}>
            {!selected || !path ? (
              <EmptyState icon={IconFolderOpen} title="Choose a folder" description="Pick a folder on the left to see its files." />
            ) : (
              <>
                <Group justify="space-between" p="md">
                  <Stack gap={0}>
                    <Text fz="xs" c="dimmed">
                      {path.slice(0, -1).join(' / ')}
                    </Text>
                    <Text fw={600}>{path[path.length - 1]}</Text>
                  </Stack>
                  {can(module, 'edit') && (
                    <Button size="sm" leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)}>
                      Upload
                    </Button>
                  )}
                </Group>
                {files.isLoading ? (
                  <LoadingState />
                ) : !files.data?.items.length ? (
                  <EmptyState icon={IconFolder} title="No files yet" description={can(module, 'edit') ? 'Use Upload to add the first file.' : undefined} />
                ) : (
                  <div className="table-scroll">
                    <Table miw={560}>
                      <Table.Tbody>
                        {files.data.items.map((f) => (
                          <Table.Tr key={f.id}>
                            <Table.Td maw={480}>
                              <FileRow file={f} />
                            </Table.Td>
                            <Table.Td>
                              <Text fz="xs" c="dimmed" className="nowrap">
                                {formatDate(f.uploadedAt)}
                              </Text>
                            </Table.Td>
                            <Table.Td w={40}>
                              {can(module, 'delete') && (
                                <Tooltip label="Remove">
                                  <ActionIcon color="red" onClick={() => remove.mutate(f.id)} aria-label={`Remove ${f.name}`}>
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Card>
        </Grid.Col>
      </Grid>
      <Modal opened={adding} onClose={() => setAdding(false)} title={`Upload to ${path?.[path.length - 1] ?? ''}`} size="lg">
        <Stack>
          {selected && <FileUploader area={library} context={{ nodeKey: selected.key }} value={pending} onChange={setPending} onBusyChange={setBusy} />}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button disabled={!pending.length || busy} loading={attach.isPending} onClick={() => attach.mutate()}>
              Save files
            </Button>
          </Group>
        </Stack>
      </Modal>
      {library === 'pharmacopoeia' && <CountryPicker opened={picking} onClose={() => setPicking(false)} onPick={setSelected} />}
    </>
  );
}
